import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { PROXY_API_PATH } from '../constants';

const DEFAULT_BRAND = {
    siteName: '',
    logoUrl: '',          // square icon — favicon, emails, compact spots
    logoWideUrl: '',      // horizontal lockup (icon + name baked in) — navbar/footer
    logoWideDarkUrl: '',  // dark-theme twin of the lockup (falls back to logoWideUrl)
    logoMode: 'classic',  // 'classic' = icon + text name (default) · 'wide' = lockup image only
};

const SiteBrandContext = createContext();

export const useSiteBrand = () => {
    const ctx = useContext(SiteBrandContext);
    if (!ctx) throw new Error('useSiteBrand must be used within a SiteBrandProvider');
    return ctx;
};

export const SiteBrandProvider = ({ children }) => {
    const [brand, setBrand] = useState(() => {
        // 1. Prefer the server-injected brand (no async delay, no stale cache).
        //    The server writes window.__HM_BRAND__ into every HTML response so
        //    the correct tenant name and logo are available on the very first paint.
        try {
            if (window.__HM_BRAND__?.siteName || window.__HM_BRAND__?.logoUrl) {
                return { ...DEFAULT_BRAND, ...window.__HM_BRAND__ };
            }
        } catch {}
        // 2. Fall back to localStorage for repeat visitors whose session is warm.
        try {
            const saved = localStorage.getItem('hm_site_brand');
            return saved ? { ...DEFAULT_BRAND, ...JSON.parse(saved) } : DEFAULT_BRAND;
        } catch {
            return DEFAULT_BRAND;
        }
    });

    const updateBrand = useCallback((updates) => {
        setBrand(prev => {
            const next = { ...prev, ...updates };
            try { localStorage.setItem('hm_site_brand', JSON.stringify(next)); } catch {}
            // Also persist to server so each tenant domain keeps its own branding
            try {
                fetch(`${PROXY_API_PATH}/admin/site-settings`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'x-admin-token': localStorage.getItem('hm_admin_token') || '' },
                    body: JSON.stringify({ key: 'hm_site_brand', value: next }),
                }).catch(() => {});
            } catch {}
            return next;
        });
    }, []);

    // Brand is loaded by App.js startup fetch (hm-settings-loaded event below).
    // No separate fetch needed here — avoids a duplicate /api/public-config call.

    // Brand name as the tab title only when the page has no title of its own
    // (or still shows a brand / build default). It used to overwrite every
    // page's server-rendered title ("Guided Umrah Package | Holidays Master")
    // with the bare site name once the brand loaded.
    const lastBrandTitle = useRef('');
    useEffect(() => {
        if (!brand.siteName) return;
        const cur = String(document.title || '').trim();
        if (!cur || cur === lastBrandTitle.current || cur === 'React App' || cur === 'Holidays Master') {
            document.title = brand.siteName;
        }
        lastBrandTitle.current = brand.siteName;
    }, [brand.siteName]);

    // Update browser tab icon (favicon) when the tenant logo changes.
    // Browsers (Chrome especially) cache favicons very aggressively, so we:
    //   1. Update EVERY icon-related <link> in the document head
    //   2. Set the modern `image/x-icon` AND `image/png` types so the browser
    //      prefers the explicit logo over any cached default
    //   3. Append a cache-buster query so the browser re-fetches even if it
    //      already has a stale icon for this URL
    //   4. Remove any `link[rel="shortcut icon"]` (legacy IE-era tag) that
    //      might still be holding the React default
    useEffect(() => {
        if (!brand.logoUrl) return;
        const cacheBuster = brand.logoUrl.includes('?')
            ? `${brand.logoUrl}&v=${Date.now()}`
            : `${brand.logoUrl}?v=${Date.now()}`;

        const upsertIconLink = (rel, type) => {
            // Remove existing matches first so we don't end up with stale duplicates
            document.querySelectorAll(`link[rel='${rel}']`).forEach(el => el.remove());
            const link = document.createElement('link');
            link.rel = rel;
            if (type) link.type = type;
            link.href = cacheBuster;
            document.head.appendChild(link);
        };

        upsertIconLink('icon', 'image/png');           // primary modern favicon
        upsertIconLink('shortcut icon');                // legacy IE-era tag, replaced
        upsertIconLink('apple-touch-icon');             // iOS home screen
    }, [brand.logoUrl]);

    // Keep in sync across tabs on the same browser
    useEffect(() => {
        const handleStorage = (e) => {
            if (e.key === 'hm_site_brand' && e.newValue) {
                try { setBrand({ ...DEFAULT_BRAND, ...JSON.parse(e.newValue) }); } catch {}
            }
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    // Re-apply brand after App.js pre-loads tenant settings from server
    useEffect(() => {
        const onLoaded = () => {
            try {
                const saved = localStorage.getItem('hm_site_brand');
                if (saved) setBrand({ ...DEFAULT_BRAND, ...JSON.parse(saved) });
            } catch {}
        };
        window.addEventListener('hm-settings-loaded', onLoaded);
        return () => window.removeEventListener('hm-settings-loaded', onLoaded);
    }, []);
    // Memoised — see ThemeContext.
    const brandValue = useMemo(() => ({ brand, updateBrand }), [brand, updateBrand]);

    return (
        <SiteBrandContext.Provider value={brandValue}>
            {children}
        </SiteBrandContext.Provider>
    );
};
